import { MessageThread } from "./MessageThread.tsx";
import { ReplyComposer } from "./ReplyComposer.tsx";
import { TicketHeader } from "./TicketHeader.tsx";
import { TicketSidebar } from "./TicketSidebar.tsx";
import { ticketContactEmails, ticketDisplayTitle } from "../utils/ticketDisplay.ts";
import type { Channel, EmailLog, Message, ScheduleSaveResult, Ticket } from "../types.ts";
import type { MentionUser } from "../utils/messageDisplay.ts";

export function TicketDetailPanel({
  ticket,
  messages,
  emailLog,
  transitions,
  ticketViewers,
  moreActionsOpen,
  moreActionsRef,
  aiMenuOpen,
  aiMenuRef,
  msgEndRef,
  reply,
  replyToCustomer,
  mentionUsers,
  currentAuthorEmail,
  currentAuthorRole,
  aiBusy,
  aiSummary,
  sending,
  canWrite,
  canEditChannel,
  onGoToDevice,
  onStatusChange,
  onAssigneeChange,
  onChannelChange,
  onTitleChange,
  onMoreActionsToggle,
  onTriage,
  onCorrection,
  onJunkClose,
  onSaveContacts,
  onScheduleSaved,
  onTasksChanged,
  onAssetChanged,
  onReplyChange,
  onReplyToCustomerChange,
  onAiMenuToggle,
  onAiDraft,
  onAiSummarize,
  onAiFaqSuggest,
  faqSuggestEnabled,
  onKbOpen,
  onClearAiSummary,
  onSend,
  railOpen,
  onRailToggle,
  onAssignSelf,
  onBackToList,
  onEditContacts,
  draftSavedAt,
}: {
  ticket: Ticket;
  messages: Message[];
  emailLog: EmailLog[];
  transitions: Record<string, string[]>;
  ticketViewers: string[];
  moreActionsOpen: boolean;
  moreActionsRef: React.RefObject<HTMLDivElement | null>;
  aiMenuOpen: boolean;
  aiMenuRef: React.RefObject<HTMLDivElement | null>;
  msgEndRef: React.RefObject<HTMLDivElement | null>;
  reply: string;
  replyToCustomer: boolean;
  mentionUsers: MentionUser[];
  currentAuthorEmail: string | null;
  currentAuthorRole: string | null;
  aiBusy: null | "draft" | "summary" | "faq";
  aiSummary: string;
  sending: boolean;
  canWrite: boolean;
  canEditChannel: boolean;
  onGoToDevice: (serial: string) => void;
  onStatusChange: (status: string) => void;
  onAssigneeChange: (email: string | null) => void;
  onChannelChange: (channel: Channel) => void;
  onTitleChange: (title: string) => void | Promise<void>;
  onMoreActionsToggle: () => void;
  onTriage: () => void;
  onCorrection: () => void;
  onJunkClose?: () => void;
  onSaveContacts: (fields: { contact_emails: string[]; contact_phones: string[] }) => Promise<void>;
  onScheduleSaved: (result: ScheduleSaveResult) => void;
  onTasksChanged?: (result?: import("../types.ts").SupportTask) => void;
  onAssetChanged?: (result?: import("../utils/threadMessage.ts").HasThreadMessage) => void;
  onReplyChange: (v: string) => void;
  onReplyToCustomerChange: (v: boolean) => void;
  onAiMenuToggle: () => void;
  onAiDraft: () => void;
  onAiSummarize: () => void;
  onAiFaqSuggest: () => void;
  faqSuggestEnabled: boolean;
  onKbOpen: () => void;
  onClearAiSummary: () => void;
  onSend: (attachmentFiles: File[], toEmails?: string[]) => void | Promise<void>;
  /** 情報レール（タスク/連絡先/機体手配/現場予定/起票内容/メール配送ログ）の開閉（改善 B-1） */
  railOpen: boolean;
  onRailToggle: () => void;
  onAssignSelf?: () => void;
  /** 1200px 未満で一覧へ戻る（改善 B-3） */
  onBackToList?: () => void;
  /** 宛先バーの「変更」から連絡先セクションを開く（改善 F-2） */
  onEditContacts?: () => void;
  /** 本文の localStorage 自動保存時刻 */
  draftSavedAt?: number | null;
}) {
  // 自動返信・ステータス通知を除き、担当者が顧客へ送るのが初めてか（改善 F-3）
  const AUTO_SOURCES = new Set(["auto_ack", "urgent_ack", "intake_ack", "status_notify", "system"]);
  const isFirstCustomerReply = !messages.some(
    (m) => m.source !== "email" && !AUTO_SOURCES.has(m.source) && (m.visibility ?? "internal") === "customer",
  );
  return (
    <div className="support-ticket-detail" key={ticket.id}>
      <TicketHeader
        ticket={ticket}
        displayTitle={ticketDisplayTitle(ticket)}
        ticketViewers={ticketViewers}
        transitions={transitions}
        mentionUsers={mentionUsers}
        canWrite={canWrite}
        canEditChannel={canEditChannel}
        moreActionsOpen={moreActionsOpen}
        moreActionsRef={moreActionsRef}
        onGoToDevice={onGoToDevice}
        onStatusChange={onStatusChange}
        onAssigneeChange={onAssigneeChange}
        onChannelChange={onChannelChange}
        onTitleChange={onTitleChange}
        onMoreActionsToggle={onMoreActionsToggle}
        onTriage={onTriage}
        onCorrection={onCorrection}
        onJunkClose={onJunkClose}
        onAssignSelf={onAssignSelf}
        railOpen={railOpen}
        onRailToggle={onRailToggle}
        hasOpenTasks={(ticket.open_task_count ?? 0) > 0}
        onBackToList={onBackToList}
      />
      <div className={"support-ticket-detail__body" + (railOpen ? "" : " support-ticket-detail__body--rail-closed")}>
        <div className="support-ticket-detail__main">
          <MessageThread
            messages={messages}
            msgEndRef={msgEndRef}
            currentAuthorEmail={currentAuthorEmail}
            currentAuthorRole={currentAuthorRole}
          />
          <ReplyComposer
            ticketId={ticket.id}
            reply={reply}
            replyToCustomer={replyToCustomer}
            contactEmail={ticket.contact_email}
            contactName={ticket.contact_name}
            contactEmails={ticketContactEmails(ticket)}
            isFirstCustomerReply={isFirstCustomerReply}
            onEditContacts={onEditContacts}
            mentionUsers={mentionUsers}
            aiBusy={aiBusy}
            aiMenuOpen={aiMenuOpen}
            aiMenuRef={aiMenuRef}
            aiSummary={aiSummary}
            sending={sending}
            draftSavedAt={draftSavedAt}
            onReplyChange={onReplyChange}
            onReplyToCustomerChange={onReplyToCustomerChange}
            onAiMenuToggle={onAiMenuToggle}
            onAiDraft={onAiDraft}
            onAiSummarize={onAiSummarize}
            onAiFaqSuggest={onAiFaqSuggest}
            faqSuggestEnabled={faqSuggestEnabled}
            onKbOpen={onKbOpen}
            onClearAiSummary={onClearAiSummary}
            onSend={onSend}
          />
        </div>
        {railOpen && (
          <TicketSidebar
            ticket={ticket}
            emailLog={emailLog}
            mentionUsers={mentionUsers}
            canWrite={canWrite}
            onSaveContacts={onSaveContacts}
            onScheduleSaved={onScheduleSaved}
            onTasksChanged={onTasksChanged}
            onAssetChanged={onAssetChanged}
            onClose={onRailToggle}
          />
        )}
      </div>
    </div>
  );
}
